import { Link, createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { CheckCircle, Loader2, Lock } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { AuthLayout } from '@/components/layouts/AuthLayout'
import { supabase } from '@/auth/supabase'

export const Route = createFileRoute('/_authenticated/account/update-password')({
  component: RouteComponent,
})

function RouteComponent() {
  const [passwordUpdated, setPasswordUpdated] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<{
    newPassword?: string
    confirmPassword?: string
  }>({})

  const updatePasswordMutation = useMutation({
    mutationFn: async (password: string) => {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) return { error: true, message: error.message }
      return { error: false, message: 'Password updated successfully' }
    },
    onSuccess: (data) => {
      if (!data.error) {
        setPasswordUpdated(true)
        setNewPassword('')
        setConfirmPassword('')
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})

    const validationErrors: typeof errors = {}

    if (!newPassword) {
      validationErrors.newPassword = 'Password is required'
    } else if (newPassword.length < 6) {
      validationErrors.newPassword = 'Password must be at least 6 characters'
    }

    if (!confirmPassword) {
      validationErrors.confirmPassword = 'Please confirm your password'
    } else if (newPassword !== confirmPassword) {
      validationErrors.confirmPassword = 'Passwords do not match'
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    updatePasswordMutation.mutate(newPassword)
  }

  return (
    <AuthLayout
      title="Set New Password"
      description="Choose a new password for your account"
    >
      {passwordUpdated ? (
        <div className="flex flex-col items-center justify-center py-8 gap-4">
          <CheckCircle className="w-12 h-12 text-ice" />
          <p className="text-center text-sm text-muted-foreground">
            Your password has been updated successfully!
          </p>
          <Link to="/dashboard">
            <Button
              variant="outline"
              className="border-ice/30 hover:border-ice/50"
            >
              Back to Dashboard
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="newPassword"
                type="password"
                name="newPassword"
                placeholder="Enter new password"
                className={`pl-10 bg-secondary/50 border-ice/20 ${errors.newPassword ? 'border-red-500' : ''}`}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value)
                  if (errors.newPassword) {
                    setErrors((prev) => ({ ...prev, newPassword: undefined }))
                  }
                }}
                required
              />
            </div>
            {errors.newPassword && (
              <p className="text-xs text-red-400">{errors.newPassword}</p>
            )}
            {!errors.newPassword && (
              <p className="text-xs text-muted-foreground">
                Must be at least 6 characters
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                placeholder="Confirm new password"
                className={`pl-10 bg-secondary/50 border-ice/20 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value)
                  if (errors.confirmPassword) {
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: undefined,
                    }))
                  }
                }}
                required
              />
            </div>
            {errors.confirmPassword && (
              <p className="text-xs text-red-400">{errors.confirmPassword}</p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90"
            disabled={updatePasswordMutation.isPending}
          >
            {updatePasswordMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating password...
              </>
            ) : (
              'Update Password'
            )}
          </Button>

          {updatePasswordMutation.data?.error && (
            <div className="text-red-400 text-sm text-center">
              {updatePasswordMutation.data.message}
            </div>
          )}
        </form>
      )}
    </AuthLayout>
  )
}
